import React, { useCallback, type ReactNode } from 'react';
import Link from '@atlaskit/link';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import type { ApplicationLinkV3Status } from '../../../../../common/entities.tsx';
import {
	AUTH_DISABLED,
	AUTH_OAUTH1,
	AUTH_OAUTH1_IMPERSONATION,
	AUTH_UNKNOWN,
	getAuthLevel,
} from '../../../../../common/status/oauth1-match.tsx';
import messages from '../../messages.tsx';

export type LocalRemoteMismatchState =
	| 'disabled-oauth'
	| 'disabled-impersonation'
	| 'oauth-disabled'
	| 'oauth-impersonation'
	| 'impersonation-disabled'
	| 'impersonation-oauth'
	| 'unknown'
	| 'no-mismatch';

export type AuthDirection = 'incoming' | 'outgoing';

type PerDirectionDescriptionProps = {
	mismatchState: LocalRemoteMismatchState;
	direction: AuthDirection;
	displayUrl: string;
	name: string;
};

const PerDirectionDescription = (props: PerDirectionDescriptionProps) => {
	const { formatMessage } = useIntl();

	const buildDescriptionForMismatchState = useCallback(
		(direction: AuthDirection) => {
			const directionString =
				direction === 'incoming'
					? formatMessage(messages.authLevelMismatchIncomingNonfinal)
					: formatMessage(messages.authLevelMismatchOutgoingNonfinal);

			switch (props.mismatchState) {
				case 'disabled-oauth':
				case 'disabled-impersonation': {
					const remoteAuthTypeString =
						props.mismatchState === 'disabled-oauth'
							? formatMessage(messages.authTypeDisabledNonfinal)
							: formatMessage(messages.authTypeImpersonationNonfinal);

					return formatMessage(messages.authLevelMismatchDisabledOauthDescriptionNonfinal, {
						direction: directionString,
						applicationName: props.name,
						remoteAuthType: remoteAuthTypeString,
						link: (chunks: ReactNode[]) => (
							<Link href={props.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					});
				}

				case 'oauth-disabled':
				case 'impersonation-disabled': {
					const localAuthTypeString =
						props.mismatchState === 'oauth-disabled'
							? 'OAuth' // not translated, technical term
							: formatMessage(messages.authTypeImpersonationNonfinal);

					return formatMessage(messages.authLevelMismatchOauthDisabledDescriptionNonfinal, {
						direction: directionString,
						applicationName: props.name,
						localAuthType: localAuthTypeString,
						link: (chunks: ReactNode[]) => (
							<Link href={props.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					});
				}

				case 'oauth-impersonation':
				case 'impersonation-oauth': {
					const localAuthTypeString =
						props.mismatchState === 'oauth-impersonation'
							? 'OAuth' // not translated, technical term
							: formatMessage(messages.authTypeImpersonationNonfinal);
					const remoteAuthTypeString =
						props.mismatchState === 'oauth-impersonation'
							? formatMessage(messages.authTypeImpersonationNonfinal)
							: 'OAuth'; // not translated, technical term

					return formatMessage(messages.authLevelMismatchOauthOauthDescriptionNonfinal, {
						direction: directionString,
						applicationName: props.name,
						localAuthType: localAuthTypeString,
						remoteAuthType: remoteAuthTypeString,
						link: (chunks: ReactNode[]) => (
							<Link href={props.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					});
				}

				default:
					return <></>;
			}
		},
		[formatMessage, props.displayUrl, props.mismatchState, props.name],
	);

	return <Box>{buildDescriptionForMismatchState(props.direction)}</Box>;
};

const determineMismatchState = (
	status: ApplicationLinkV3Status,
	direction: AuthDirection,
): LocalRemoteMismatchState => {
	const local = getAuthLevel(status.localAuthentication?.[direction]);
	const remote = getAuthLevel(status.remoteAuthentication?.[direction]);

	if (local === AUTH_UNKNOWN || remote === AUTH_UNKNOWN) {
		return 'unknown';
	}

	if (local === AUTH_DISABLED && remote === AUTH_OAUTH1) {
		return 'disabled-oauth';
	}
	if (local === AUTH_DISABLED && remote === AUTH_OAUTH1_IMPERSONATION) {
		return 'disabled-impersonation';
	}
	if (local === AUTH_OAUTH1 && remote === AUTH_DISABLED) {
		return 'oauth-disabled';
	}
	if (local === AUTH_OAUTH1_IMPERSONATION && remote === AUTH_DISABLED) {
		return 'impersonation-disabled';
	}
	if (local === AUTH_OAUTH1_IMPERSONATION && remote === AUTH_DISABLED) {
		return 'impersonation-disabled';
	}
	if (local === AUTH_OAUTH1_IMPERSONATION && remote === AUTH_OAUTH1) {
		return 'impersonation-oauth';
	}

	return 'no-mismatch';
};

type InnerDescriptionProps = {
	incomingMismatch: LocalRemoteMismatchState;
	outgoingMismatch: LocalRemoteMismatchState;
	displayUrl: string;
	name: string;
};

export const InnerDescription = (props: InnerDescriptionProps) => {
	const { formatMessage } = useIntl();

	if (props.incomingMismatch === 'unknown' || props.outgoingMismatch === 'unknown') {
		return formatMessage(messages.authLevelMismatchUnknownDescriptionNonfinal);
	}

	return (
		<Stack space="space.100">
			<PerDirectionDescription
				direction="incoming"
				mismatchState={props.incomingMismatch}
				displayUrl={props.displayUrl}
				name={props.name}
			/>
			<PerDirectionDescription
				direction="outgoing"
				mismatchState={props.outgoingMismatch}
				displayUrl={props.displayUrl}
				name={props.name}
			/>
		</Stack>
	);
};

const AuthLevelMismatchDescription = (props: { status: ApplicationLinkV3Status }) => {
	const incomingMismatch = determineMismatchState(props.status, 'incoming');
	const outgoingMismatch = determineMismatchState(props.status, 'outgoing');

	return (
		<InnerDescription
			incomingMismatch={incomingMismatch}
			outgoingMismatch={outgoingMismatch}
			displayUrl={props.status.link.displayUrl}
			name={props.status.link.name}
		/>
	);
};

export default AuthLevelMismatchDescription;
